import { DateTimeFlexible } from './DateTimeFlexible';
import { DateTimeSerializable } from './DateTimeSerializable';
import { DateTime, Schema, SchemaGetter } from 'effect';

const formatDateTime = (input: DateTime.DateTime) =>
  DateTime.isZoned(input) ? DateTime.formatIsoZoned(input) : DateTime.formatIso(input);

const DateTimeFromSelf = Schema.Union([Schema.DateTimeZoned, Schema.DateTimeUtc]);

/**
 * Schema that transforms DateTime.Utc | DateTime.Zoned → DateTimeSerializable.
 *
 * Encoded: DateTime.Utc | DateTime.Zoned (FromSelf — no string transformation)
 * Type: DateTimeSerializable
 *
 * Compose with `DateTimeFlexible` via `.pipe(Schema.decodeTo(...))` to go
 * from string → DateTimeSerializable (v4: `Schema.compose` was removed).
 */
// v4: `Schema.decodeTo(target, transformation)` takes `{decode, encode}` where
// each is a `Getter`, not the v3 `SchemaTransformation.transform(...)` wrapper.
export const DateTimeSerializableFromDateTimeFlexible = DateTimeFromSelf.pipe(
  Schema.decodeTo(DateTimeSerializable, {
    decode: SchemaGetter.transform((input: DateTime.DateTime) =>
      DateTimeSerializable.make({
        _tag: 'DateTime',
        value: formatDateTime(input),
      })
    ),
    encode: SchemaGetter.transform((input: DateTimeSerializable) =>
      Schema.decodeSync(DateTimeFlexible)(input.value)
    ),
  })
);

export type DateTimeSerializableFromDateTimeFlexible = Schema.Schema.Type<
  typeof DateTimeSerializableFromDateTimeFlexible
>;

/**
 * Schema that transforms DateTimeSerializable → DateTime.Utc | DateTime.Zoned.
 * This is the inverse of DateTimeSerializableFromDateTimeFlexible.
 *
 * Encoded: DateTimeSerializable
 * Type: DateTime.Utc | DateTime.Zoned
 */
export const DateTimeFlexibleFromDateTimeSerializable = DateTimeSerializable.pipe(
  Schema.decodeTo(DateTimeFromSelf, {
    decode: SchemaGetter.transform((input: DateTimeSerializable) =>
      Schema.decodeSync(DateTimeFlexible)(input.value)
    ),
    encode: SchemaGetter.transform((input: DateTime.DateTime) =>
      DateTimeSerializable.make({
        _tag: 'DateTime',
        value: formatDateTime(input),
      })
    ),
  })
);

export type DateTimeFlexibleFromDateTimeSerializable = Schema.Schema.Type<
  typeof DateTimeFlexibleFromDateTimeSerializable
>;

export const decodeSyncDateTimeFlexibleFromDateTimeSerializable = Schema.decodeSync(
  DateTimeFlexibleFromDateTimeSerializable
);
export const encodeSyncDateTimeFlexibleFromDateTimeSerializable = Schema.encodeSync(
  DateTimeFlexibleFromDateTimeSerializable
);
